/**
 * Bangla number & date formatting helpers.
 *
 * Uses `Intl` with the `bn-BD` locale so numbers are rendered with Bengali
 * digits (০১২৩৪৫৬৭৮৯) and dates follow Bangladeshi conventions. Falls back
 * to English rendering when `lang !== "bn"`.
 */

export type Lang = "en" | "bn";

const BN_DIGITS = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"] as const;

/** Manual digit swap — avoids Intl in server contexts and hot render paths. */
export function toBnDigits(input: string | number): string {
  return String(input).replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]);
}

export function formatNumber(n: number, lang: Lang = "en"): string {
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat(lang === "bn" ? "bn-BD" : "en-US").format(n);
  } catch {
    return lang === "bn" ? toBnDigits(n) : String(n);
  }
}

export function formatDate(value: string | number | Date, lang: Lang = "en"): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(lang === "bn" ? "bn-BD" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

export function formatDateTime(value: string | number | Date, lang: Lang = "en"): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(lang === "bn" ? "bn-BD" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

/**
 * Format a Bangladeshi phone number for display. Accepts raw digits or
 * +8801XXXXXXXXX. Bangla digits when lang="bn".
 */
export function formatBdPhone(raw: string | null | undefined, lang: Lang = "en"): string {
  if (!raw) return "";
  const digits = raw.replace(/[^\d]/g, "");
  const local = digits.startsWith("880") ? digits.slice(3) : digits;
  const grouped =
    local.length === 11 ? `${local.slice(0, 5)} ${local.slice(5, 8)} ${local.slice(8)}` : local;
  const withPrefix = `+880 ${grouped}`;
  return lang === "bn" ? toBnDigits(withPrefix) : withPrefix;
}
